import { useState, useCallback } from 'react';

const STORAGE_KEY = 'survivor_progress';

export const ALL_RARITIES = ['common', 'uncommon', 'rare', 'epic', 'legendary'];

function load() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      // Migration: old save had 'upgrades' instead of 'activatedTalents'
      if (parsed.upgrades && !parsed.activatedTalents) {
        parsed.activatedTalents = ['origin'];
        delete parsed.upgrades;
      }
      // Migration: ensure playerLevel exists
      if (!parsed.playerLevel) {
        parsed.playerLevel = 1;
      }
      // Migration: ensure lootFilter exists
      if (!parsed.lootFilter) {
        parsed.lootFilter = ALL_RARITIES;
      }
      return parsed;
    }
  } catch {}
  return { totalGold: 0, talentPoints: 0, playerLevel: 1, activatedTalents: ['origin'], leaderboard: [], lastDeathTier: 0, potionCount: 0, lootFilter: ALL_RARITIES };
}

function save(data) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {}
}

/**
 * Manages persistent cross-run progress:
 *  - totalGold:        gold bank (earned from runs, spent selling items in shop)
 *  - talentPoints:     points earned by levelling up during runs (1 per level-up)
 *  - playerLevel:      cumulative account level (starts at 1, +N for every N level-ups earned)
 *  - activatedTalents: array of activated talent node IDs ('origin' always first)
 *  - leaderboard:      top-10 run entries sorted by score desc
 */
export function usePersistentProgress() {
  const [progress, setProgress] = useState(load);

  // ── Gold (item economy) ───────────────────────────────────────────────────
  const addGold = useCallback((amount) => {
    setProgress(prev => {
      const next = { ...prev, totalGold: (prev.totalGold ?? 0) + amount };
      save(next);
      return next;
    });
  }, []);

  const spendGold = useCallback((amount) => {
    setProgress(prev => {
      const next = { ...prev, totalGold: Math.max(0, (prev.totalGold ?? 0) - amount) };
      save(next);
      return next;
    });
  }, []);

  // ── Talent points (level-up currency) ────────────────────────────────────
  /** Bank talent points earned from level-ups at the end of a run. */
  const addTalentPoints = useCallback((amount) => {
    if (!amount || amount <= 0) return;
    setProgress(prev => {
      const next = { ...prev, talentPoints: (prev.talentPoints ?? 0) + amount };
      save(next);
      return next;
    });
  }, []);

  /** Spend talent points when activating a node. */
  const spendTalentPoints = useCallback((amount) => {
    setProgress(prev => {
      const next = { ...prev, talentPoints: Math.max(0, (prev.talentPoints ?? 0) - amount) };
      save(next);
      return next;
    });
  }, []);

  // ── Player level (cumulative, never decreases) ────────────────────────────
  /**
   * Increment the persistent player level by n (called at end of each run).
   * playerLevel starts at 1 and grows by the number of level-ups earned per run.
   */
  const addPlayerLevel = useCallback((amount) => {
    if (!amount || amount <= 0) return;
    setProgress(prev => {
      const next = { ...prev, playerLevel: (prev.playerLevel ?? 1) + amount };
      save(next);
      return next;
    });
  }, []);

  // ── Talent tree ───────────────────────────────────────────────────────────
  const activateTalent = useCallback((nodeId) => {
    setProgress(prev => {
      const already = prev.activatedTalents ?? ['origin'];
      if (already.includes(nodeId)) return prev;
      const next = { ...prev, activatedTalents: [...already, nodeId] };
      save(next);
      return next;
    });
  }, []);

  /**
   * Reset all talents back to only the origin node.
   * The caller is responsible for refunding points via addTalentPoints().
   */
  const resetTalents = useCallback(() => {
    setProgress(prev => {
      const next = { ...prev, activatedTalents: ['origin'] };
      save(next);
      return next;
    });
  }, []);

  // ── Potions ───────────────────────────────────────────────────────────────
  /** Increment stored potion count by 1 (caller must pre-check max cap). */
  const addPotion = useCallback(() => {
    setProgress(prev => {
      const next = { ...prev, potionCount: (prev.potionCount ?? 0) + 1 };
      save(next);
      return next;
    });
  }, []);

  /** Decrement stored potion count by 1 (min 0). */
  const consumePotion = useCallback(() => {
    setProgress(prev => {
      if ((prev.potionCount ?? 0) <= 0) return prev;
      const next = { ...prev, potionCount: prev.potionCount - 1 };
      save(next);
      return next;
    });
  }, []);

  // ── Loot filter ───────────────────────────────────────────────────────────
  /** Persist a new loot-filter array (array of enabled rarity strings). */
  const setLootFilter = useCallback((rarities) => {
    setProgress(prev => {
      const next = { ...prev, lootFilter: rarities };
      save(next);
      return next;
    });
  }, []);

  // ── Last death tier ───────────────────────────────────────────────────────
  const saveLastDeathTier = useCallback((tier) => {
    setProgress(prev => {
      const next = { ...prev, lastDeathTier: tier ?? 0 };
      save(next);
      return next;
    });
  }, []);

  // ── Leaderboard ───────────────────────────────────────────────────────────
  const addRunToLeaderboard = useCallback((entry) => {
    setProgress(prev => {
      const leaderboard = [...(prev.leaderboard ?? []), entry]
        .sort((a, b) => b.score - a.score)
        .slice(0, 10);
      const next = { ...prev, leaderboard };
      save(next);
      return next;
    });
  }, []);

  return {
    totalGold:         progress.totalGold          ?? 0,
    talentPoints:      progress.talentPoints       ?? 0,
    playerLevel:       progress.playerLevel        ?? 1,
    activatedTalents:  progress.activatedTalents   ?? ['origin'],
    leaderboard:       progress.leaderboard        ?? [],
    lastDeathTier:     progress.lastDeathTier      ?? 0,
    potionCount:       progress.potionCount        ?? 0,
    lootFilter:        progress.lootFilter         ?? ALL_RARITIES,
    addGold,
    spendGold,
    addTalentPoints,
    spendTalentPoints,
    addPlayerLevel,
    activateTalent,
    resetTalents,
    addRunToLeaderboard,
    saveLastDeathTier,
    addPotion,
    consumePotion,
    setLootFilter,
  };
}
